import { ConflictException, Injectable, NotFoundException } from '@nestjs/common';
import { PersonasService } from 'src/personas/personas.service';
import { PrismaService } from 'src/prisma/prisma.service';
import { CreateAgenteTransporteDto } from './dto/create-agente-transporte.dto';
import { UpdateAgenteTransporteDto } from './dto/update-agente-transporte.dto';

const include = {
  personas: {
    include: {
      tipo_documento_identidad: true,
      naturaleza_receptor: true,
      ciudades: { include: { distritos: { include: { departamentos: true } } } },
    },
  },
};

@Injectable()
export class AgentesTransporteService {
  constructor(
    private prisma: PrismaService,
    private personasService: PersonasService,
  ) {}

  async create(dto: CreateAgenteTransporteDto, empresa_id: string) {
    const persona = await this.personasService.create(dto, empresa_id);
    const existing = await this.prisma.agentes_transporte.findUnique({ where: { persona_id: persona.id } });
    if (existing) throw new ConflictException('El agente de transporte ya existe para esa persona');

    return this.prisma.agentes_transporte.create({
      data: {
        empresa_id,
        persona_id: persona.id,
        tipo_agente: dto.tipo_agente ?? 'transporte',
        nro_habilitacion_aduana: dto.nro_habilitacion_aduana,
        activo: dto.activo ?? true,
      },
      include,
    });
  }

  async findAll(empresa_id: string, page = 1, limit = 20, activo?: boolean) {
    const where: any = { empresa_id };
    if (activo !== undefined) where.activo = activo;
    const [items, total] = await Promise.all([
      this.prisma.agentes_transporte.findMany({
        where,
        include,
        skip: (page - 1) * limit,
        take: limit,
        orderBy: { created_at: 'desc' },
      }),
      this.prisma.agentes_transporte.count({ where }),
    ]);
    return { items, total, page, limit, lastPage: Math.ceil(total / limit) };
  }

  async search(empresa_id: string, q: string, limit = 20) {
    return this.prisma.agentes_transporte.findMany({
      where: {
        empresa_id,
        activo: true,
        personas: {
          OR: [
            { razon_social: { contains: q, mode: 'insensitive' } },
            { ruc: { contains: q, mode: 'insensitive' } },
            { nro_documento: { contains: q, mode: 'insensitive' } },
          ],
        },
      },
      include,
      take: limit,
    });
  }

  async findOne(id: string, empresa_id: string) {
    const item = await this.prisma.agentes_transporte.findFirst({ where: { id, empresa_id }, include });
    if (!item) throw new NotFoundException('Agente de transporte no encontrado');
    return item;
  }

  async update(id: string, empresa_id: string, dto: UpdateAgenteTransporteDto) {
    const item = await this.findOne(id, empresa_id);
    await this.personasService.update(item.persona_id, dto as any, empresa_id);
    return this.prisma.agentes_transporte.update({
      where: { id },
      data: {
        tipo_agente: dto.tipo_agente,
        nro_habilitacion_aduana: dto.nro_habilitacion_aduana,
        activo: dto.activo,
        updated_at: new Date(),
      },
      include,
    });
  }

  async remove(id: string, empresa_id: string) {
    await this.findOne(id, empresa_id);
    return this.prisma.agentes_transporte.update({
      where: { id },
      data: { activo: false, updated_at: new Date() },
    });
  }
}
